import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { KeyRound, Pencil, Plus, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PERMISSION_LABELS = {
  view: "Görüntüleme",
  create: "Kayıt ekleme",
  edit: "Düzenleme",
  delete: "Silme",
  groups: "Grup/kalem yönetimi",
  rates: "Kur ayarı",
  reports: "Rapor / PDF / e-posta",
};
const PERMISSION_KEYS = Object.keys(PERMISSION_LABELS);

const blank = { username: "", name: "", email: "", password: "", role: "user", permissions: ["view"] };

function PermissionGrid({ value, onChange, disabled, prefix }) {
  const toggle = key => onChange(value.includes(key) ? value.filter(p => p !== key) : [...value, key]);
  return (
    <div className="perm-grid" data-testid={`${prefix}-permissions`}>
      {PERMISSION_KEYS.map(key => (
        <label key={key} className={`perm-item ${disabled ? "off" : ""}`}>
          <input type="checkbox" data-testid={`${prefix}-perm-${key}`} disabled={disabled}
            checked={disabled || value.includes(key)} onChange={() => toggle(key)}/>
          <span>{PERMISSION_LABELS[key]}</span>
        </label>
      ))}
    </div>
  );
}

export default function UserManager() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const [notice, setNotice] = useState({ text: "", error: false });

  const load = useCallback(() => {
    axios.get(`${API}/users`).then(r => setUsers(r.data)).catch(() => setUsers([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, ok) => {
    try {
      await fn();
      setNotice({ text: ok, error: false });
      load();
      return true;
    } catch (e) {
      setNotice({ text: e?.response?.data?.detail || "İşlem başarısız", error: true });
      return false;
    }
  };

  const addUser = async event => {
    event.preventDefault();
    const ok = await run(() => axios.post(`${API}/users`, {
      username: form.username.trim(), name: form.name.trim(), email: form.email.trim() || null,
      password: form.password, role: form.role, permissions: form.permissions,
    }), `Kullanıcı oluşturuldu: ${form.username.trim()}`);
    if (ok) setForm(blank);
  };

  const saveEditing = async () => {
    const u = editing;
    const ok = await run(() => axios.put(`${API}/users/${u.id}`, {
      name: u.name, email: u.email || "", role: u.role, permissions: u.permissions, active: u.active,
    }), `${u.username} güncellendi`);
    if (ok) setEditing(null);
  };

  const doReset = async () => {
    const ok = await run(() => axios.post(`${API}/users/${resetting.id}/password`, { new_password: newPassword }),
      `${resetting.username} için şifre sıfırlandı`);
    if (ok) { setResetting(null); setNewPassword(""); }
  };

  const confirmDelete = () => {
    const u = pendingDelete;
    setPendingDelete(null);
    run(() => axios.delete(`${API}/users/${u.id}`), `${u.username} silindi`);
  };

  return (
    <section className="panel user-manager" data-testid="user-manager">
      <div className="panel-head">
        <div>
          <p className="eyebrow">KULLANICI YÖNETİMİ</p>
          <h3>Kullanıcılar ve yetkiler</h3>
        </div>
        <span className="unit" data-testid="user-count">{users.length} kullanıcı</span>
      </div>

      {notice.text && <div className={`notice ${notice.error ? "error" : ""}`} data-testid="user-notice">{notice.text}</div>}

      <div className="table-wrap">
        <table>
          <thead>
            <tr><th>Kullanıcı</th><th>Rol</th><th>Yetkiler</th><th>Durum</th><th></th></tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} data-testid={`user-row-${u.username}`}>
                <td>
                  <b>{u.name}</b>
                  <small className="sub-line">{u.username}{u.email ? ` · ${u.email}` : ""}</small>
                </td>
                <td>
                  <span className={`type-pill ${u.role === "admin" ? "income" : "expense"}`}>
                    {u.role === "admin" ? "Yönetici" : "Kullanıcı"}
                  </span>
                </td>
                <td className="perm-cell">
                  {u.role === "admin"
                    ? <span className="group-tag income"><ShieldCheck size={11}/> Tüm yetkiler</span>
                    : (u.permissions.length
                        ? u.permissions.map(p => <span key={p} className="group-tag">{PERMISSION_LABELS[p]}</span>)
                        : <small className="hint">Yetki verilmedi</small>)}
                </td>
                <td>{u.active ? <small className="state-on">Aktif</small> : <small className="state-off">Devre dışı</small>}</td>
                <td className="row-actions">
                  <button className="icon-btn" title="Düzenle" data-testid={`edit-user-${u.username}`}
                    onClick={() => setEditing({ ...u, email: u.email || "" })}><Pencil size={15}/></button>
                  <button className="icon-btn" title="Şifre sıfırla" data-testid={`reset-user-${u.username}`}
                    onClick={() => { setResetting(u); setNewPassword(""); }}><KeyRound size={15}/></button>
                  <button className="icon-btn danger" title="Sil" data-testid={`delete-user-${u.username}`}
                    onClick={() => setPendingDelete(u)}><Trash2 size={15}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <form className="user-form" onSubmit={addUser} data-testid="new-user-form">
        <div className="user-form-head"><UserPlus size={16}/> <b>Yeni kullanıcı tanımla</b></div>
        <div className="form-row">
          <div className="field">
            <label>Kullanıcı adı</label>
            <input data-testid="new-user-username" required value={form.username}
              onChange={e => setForm({ ...form, username: e.target.value })} placeholder="ör: ayse.k"/>
          </div>
          <div className="field">
            <label>Ad soyad</label>
            <input data-testid="new-user-name" required value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ayşe Kaya"/>
          </div>
        </div>
        <div className="form-row">
          <div className="field">
            <label>E-posta <small className="hint">(isteğe bağlı, giriş için de kullanılır)</small></label>
            <input data-testid="new-user-email" type="email" value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })} placeholder="ayse@sirket.com"/>
          </div>
          <div className="field">
            <label>Geçici şifre <small className="hint">(kullanıcı ilk girişte değiştirir)</small></label>
            <input data-testid="new-user-password" required minLength={6} value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}/>
          </div>
        </div>
        <div className="field">
          <label>Rol</label>
          <select data-testid="new-user-role" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
            <option value="user">Kullanıcı</option>
            <option value="admin">Yönetici (tüm yetkiler)</option>
          </select>
        </div>
        <div className="field">
          <label>Yetkiler</label>
          <PermissionGrid prefix="new-user" disabled={form.role === "admin"} value={form.permissions}
            onChange={permissions => setForm({ ...form, permissions })}/>
        </div>
        <button className="primary-btn" type="submit" data-testid="create-user-button">Kullanıcıyı oluştur <Plus size={16}/></button>
      </form>

      <Dialog open={!!editing} onOpenChange={o => !o && setEditing(null)}>
        <DialogContent className="edit-dialog" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.username} düzenle</DialogTitle>
            <DialogDescription>Rol, yetkiler ve hesap durumunu güncelleyebilirsiniz.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="edit-form">
              <div className="field">
                <label>Ad soyad</label>
                <input data-testid="edit-user-name" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })}/>
              </div>
              <div className="field">
                <label>E-posta</label>
                <input data-testid="edit-user-email" value={editing.email} onChange={e => setEditing({ ...editing, email: e.target.value })}/>
              </div>
              <div className="field">
                <label>Rol</label>
                <select data-testid="edit-user-role" value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })}>
                  <option value="user">Kullanıcı</option>
                  <option value="admin">Yönetici (tüm yetkiler)</option>
                </select>
              </div>
              <div className="field">
                <label>Yetkiler</label>
                <PermissionGrid prefix="edit-user" disabled={editing.role === "admin"} value={editing.permissions}
                  onChange={permissions => setEditing({ ...editing, permissions })}/>
              </div>
              <label className="perm-item">
                <input type="checkbox" data-testid="edit-user-active" checked={editing.active}
                  onChange={e => setEditing({ ...editing, active: e.target.checked })}/>
                <span>Hesap aktif (kapalıysa giriş yapamaz)</span>
              </label>
              <div className="dialog-actions">
                <button type="button" className="outline-btn" data-testid="edit-user-cancel" onClick={() => setEditing(null)}>Vazgeç</button>
                <button type="button" className="primary-btn" data-testid="edit-user-save" onClick={saveEditing}>Kaydet</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={o => !o && setResetting(null)}>
        <DialogContent className="edit-dialog" data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>{resetting?.username} için şifre sıfırla</DialogTitle>
            <DialogDescription>Yeni şifreyi kullanıcıya iletin; ilk girişinde değiştirmesi istenir.</DialogDescription>
          </DialogHeader>
          <div className="edit-form">
            <div className="field">
              <label>Yeni şifre (en az 6 karakter)</label>
              <input data-testid="reset-password-input" value={newPassword} onChange={e => setNewPassword(e.target.value)}/>
            </div>
            <div className="dialog-actions">
              <button type="button" className="outline-btn" data-testid="reset-password-cancel" onClick={() => setResetting(null)}>Vazgeç</button>
              <button type="button" className="primary-btn" data-testid="reset-password-save" disabled={newPassword.length < 6} onClick={doReset}>Şifreyi kaydet</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={o => !o && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Kullanıcı silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{pendingDelete?.name} ({pendingDelete?.username})</b> hesabı kalıcı olarak silinir. Girdiği kayıtlar korunur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="danger-action" data-testid="delete-user-confirm" onClick={confirmDelete}>Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
